import { useCallback } from "react";
import { useAuth } from "./useAuth";

/**
 * Calendar API 연동 훅
 * - 캘린더 CRUD, 멤버 관리, 권한 관리
 * - status: true/false 기반 성공여부
 * - permission_denied, not_found 등 플래그 처리
 */
export default function useCalendar() {
  const { api } = useAuth();

  /** 캘린더 생성 */
  const createCalendar = useCallback(
    async ({ calendar_name, calendar_type, is_public, permissions }) => {
      try {
        const { data } = await api.post("/calendar/create", {
          calendar_name,
          calendar_type,
          is_public,
          permissions,
        });
        return { ok: !!data?.status };
      } catch (e) {
        return { ok: false };
      }
    },
    [api]
  );

  /** 캘린더 삭제 */
  const deleteCalendar = useCallback(
    async (id) => {
      try {
        const { data } = await api.post("/calendar/delete", { id });
        return {
          ok: !!data?.status,
          permissionDenied: !!data?.permission_denied,
          notFound: !!data?.not_found,
        };
      } catch (e) {
        return { ok: false };
      }
    },
    [api]
  );

  /** 캘린더 참여 멤버 목록 조회 */
  const fetchCalendarMembers = useCallback(
    async (calendarId) => {
      try {
        const { data } = await api.get(
          `/calendar/join/memberlist?cId=${calendarId}`
        );
        if (data?.permission_denied) {
          return {
            ok: false,
            permissionDenied: true,
            members: [],
            joinableMembers: [],
          };
        }
        if (data?.not_found) {
          return {
            ok: false,
            notFound: true,
            members: [],
            joinableMembers: [],
          };
        }
        if (!data?.status) {
          return { ok: false, members: [], joinableMembers: [] };
        }
        const result = data.data || {};
        const members = Array.isArray(result.join_members)
          ? result.join_members.map((m) => ({
              calendar_id: m.calendar_id,
              user_id: m.user_id,
              permission: m.permission_type || m.permission,
              user_name: m.user_name,
              is_me: m.is_me,
            }))
          : [];
        const joinableMembers = Array.isArray(result.joinable_members)
          ? result.joinable_members.map((m) => ({
              user_id: m.user_id,
              user_name: m.user_name,
            }))
          : [];
        return { ok: true, members, joinableMembers };
      } catch (e) {
        return { ok: false, members: [], joinableMembers: [] };
      }
    },
    [api]
  );

  /** 캘린더 초대 */
  const inviteToCalendar = useCallback(
    async (calendarId, permissions) => {
      try {
        const { data } = await api.post("/calendar/invite", {
          calendar_id: calendarId,
          permissions,
        });
        return {
          ok: !!data?.status,
          permissionDenied: !!data?.permission_denied,
          notFound: !!data?.not_found,
          isPublic: !!data?.public,
          isPersonal: !!data?.personal,
        };
      } catch (e) {
        return { ok: false };
      }
    },
    [api]
  );

  /** 캘린더 추방 */
  const banFromCalendar = useCallback(
    async (calendarId, banIds) => {
      try {
        const { data } = await api.post("/calendar/ban", {
          calendar_id: calendarId,
          ban_id: banIds,
        });
        return {
          ok: !!data?.status,
          permissionDenied: !!data?.permission_denied,
          notFound: !!data?.not_found,
        };
      } catch (e) {
        return { ok: false };
      }
    },
    [api]
  );

  /** 참여 가능한 캘린더 목록 조회 */
  const fetchMyCalendars = useCallback(async () => {
    try {
      const { data } = await api.get("/calendar/permission/list");
      if (!data?.status) {
        return { ok: false, calendars: [] };
      }
      const raw = Array.isArray(data.data) ? data.data : [];
      const calendars = raw.map((c) => ({
        id: c.id,
        calendar_name: c.calendar_name,
        calendar_type: c.calendar_type,
        owner_id: c.owner_id,
        permission: c.permission,
      }));
      return { ok: true, calendars };
    } catch (e) {
      return { ok: false, calendars: [] };
    }
  }, [api]);

  /** 권한 위임 (방장 권한 이전) */
  const mandatePermission = useCallback(
    async (userId, calendarId) => {
      try {
        const { data } = await api.post("/calendar/permission/mandate", {
          id: userId,
          calendar_id: calendarId,
        });
        return {
          ok: !!data?.status,
          permissionDenied: !!data?.permission_denied,
          notFound: !!data?.not_found,
          isPersonal: !!data?.personal,
        };
      } catch (e) {
        return { ok: false };
      }
    },
    [api]
  );

  /** 캘린더 떠나기 */
  const leaveCalendar = useCallback(
    async (calendarId) => {
      try {
        const { data } = await api.post("/calendar/leave", {
          calendar_id: calendarId,
        });
        return {
          ok: !!data?.status,
          notFound: !!data?.not_found,
          shouldDelete: !!data?.go_delete,
          shouldMandate: !!data?.mandate,
        };
      } catch (e) {
        return { ok: false };
      }
    },
    [api]
  );

  return {
    createCalendar,
    deleteCalendar,
    fetchCalendarMembers,
    inviteToCalendar,
    banFromCalendar,
    fetchMyCalendars,
    mandatePermission,
    leaveCalendar,
  };
}
